import { GraphPayload, createQuery } from '@camelot/server';
import { addressProps, projectProps } from '@camelot/services';

import { companyProps } from '../../organization/services/dto/company';
import { departmentProps } from '../../organization/services/dto/department';
import { PurchaseOrder, purchaseOrderProps } from './dto/purchase-order';
import { PurchaseOrderProfile, purchaseOrderProfileProps } from './dto/purchase-order-profile';
import { Reception, receptionProps, receptionRowProps } from './dto/reception';

const PURCHASE_ORDER_FIELDS = `
  ${purchaseOrderProps.get('id')}
  ${purchaseOrderProps.get('reference')}
  ${purchaseOrderProps.get('orderNumber')}
  ${purchaseOrderProps.get('externalId')}
  ${purchaseOrderProps.get('supplierReference')}
  ${purchaseOrderProps.get('type')}
  ${purchaseOrderProps.get('status')}
  ${purchaseOrderProps.get('executionStatus')}
  ${purchaseOrderProps.get('description')}
  ${purchaseOrderProps.get('supplierId')}
  ${purchaseOrderProps.get('supplierContactPersonId')}
  ${purchaseOrderProps.get('companyId')}
  ${purchaseOrderProps.get('departmentId')}
  ${purchaseOrderProps.get('projectId')}
  ${purchaseOrderProps.get('preferredDeliveryDate')}
  ${purchaseOrderProps.get('deliveryType')}
  ${purchaseOrderProps.get('deliveryAddressId')}
  ${purchaseOrderProps.get('executionStartDate')}
  ${purchaseOrderProps.get('executionEndDate')}
  ${purchaseOrderProps.get('retentionPercentage')}
  ${purchaseOrderProps.get('specificConditionsHtml')}
  ${purchaseOrderProps.get('validationDate')}
  ${purchaseOrderProps.get('sentDate')}
  ${purchaseOrderProps.get('acknowledgedDate')}
  ${purchaseOrderProps.get('parentId')}
  ${purchaseOrderProps.get('parentReference')}
  ${purchaseOrderProps.get('amendmentNumber')}
  ${purchaseOrderProps.get('budgetLineId')}
  ${purchaseOrderProps.get('budgetReference')}
  ${purchaseOrderProps.get('rowsCount')}
  ${purchaseOrderProps.get('createdTime')}
  ${purchaseOrderProps.get('updatedTime')}
`;

export function GET_PURCHASE_ORDER(id: string): GraphPayload {
  return createQuery<PurchaseOrder>('purchaseOrders', {
    prefixType: 'PurchaseOrder',
    props: `
      items {
        ${PURCHASE_ORDER_FIELDS}
      }
    `,
    where: { id: { eq: id } },
  });
}

export function GET_PURCHASE_ORDER_PROFILES(ids: string[]): GraphPayload {
  return createQuery<PurchaseOrderProfile>('purchaseOrders', {
    prefixType: 'PurchaseOrder',
    props: `
      items {
        ${purchaseOrderProfileProps.get('id')}
        ${purchaseOrderProfileProps.get('reference')}
        ${purchaseOrderProfileProps.get('company')} {
          ${companyProps.get('id')}
          ${companyProps.get('name')}
        }
        ${purchaseOrderProfileProps.get('department')} {
          ${departmentProps.get('id')}
          ${departmentProps.get('name')}
        }
        ${purchaseOrderProfileProps.get('project')} {
          ${projectProps.get('id')}
          ${projectProps.get('name')}
        }
      }
    `,
    where: { id: { in: ids } },
  });
}

const RECEPTION_ROW_FIELDS = `
  ${receptionRowProps.get('id')}
  ${receptionRowProps.get('receptionId')}
  ${receptionRowProps.get('purchaseOrderRowId')}
  ${receptionRowProps.get('purchaseOrderId')}
  ${receptionRowProps.get('purchaseOrderReference')}
  ${receptionRowProps.get('orderNumber')}
  ${receptionRowProps.get('purchaseOrderRowIndex')}
  ${receptionRowProps.get('description')}
  ${receptionRowProps.get('orderedQuantity')}
  ${receptionRowProps.get('unit')}
  ${receptionRowProps.get('unitCostPrice')}
  ${receptionRowProps.get('deliveredQuantity')}
  ${receptionRowProps.get('refusedQuantity')}
  ${receptionRowProps.get('refusalReason')}
  ${receptionRowProps.get('percentage')}
  ${receptionRowProps.get('acceptedQuantity')}
  ${receptionRowProps.get('alreadyReceived')}
  ${receptionRowProps.get('isFinalised')}
  ${receptionRowProps.get('finalisationReason')}
  ${receptionRowProps.get('overReceptionAccepted')}
  ${receptionRowProps.get('overReceptionAcceptedById')}
  ${receptionRowProps.get('overReceptionAcceptedAt')}
`;

const RECEPTION_FIELDS = `
  ${receptionProps.get('id')}
  ${receptionProps.get('reference')}
  ${receptionProps.get('receptionNumber')}
  ${receptionProps.get('status')}
  ${receptionProps.get('receptionDate')}
  ${receptionProps.get('validationDate')}
  ${receptionProps.get('cancelledDate')}
  ${receptionProps.get('cancellationReason')}
  ${receptionProps.get('supplierId')}
  ${receptionProps.get('companyId')}
  ${receptionProps.get('supplierReference')}
  ${receptionProps.get('deliveryAddressId')}
  ${receptionProps.get('deliveryAddress')} {
    ${addressProps.get('id')}
    ${addressProps.get('street')}
    ${addressProps.get('complement')}
    ${addressProps.get('postalCode')}
    ${addressProps.get('city')}
    ${addressProps.get('countryCode')}
  }
  ${receptionProps.get('receivedById')}
  ${receptionProps.get('comment')}
  ${receptionProps.get('createdTime')}
  ${receptionProps.get('updatedTime')}
  ${receptionProps.get('rows')} {
    ${RECEPTION_ROW_FIELDS}
  }
`;

// Passe par l'agrégateur : deliveryAddress est une référence fédérée.
export function GET_RECEPTION(id: string): GraphPayload {
  return createQuery<Reception>('receptions', {
    prefixType: 'Reception',
    props: `
      items {
        ${RECEPTION_FIELDS}
      }
    `,
    where: { id: { eq: id } },
  });
}
